import type { Session, User } from "@supabase/supabase-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { qk } from "./query-keys";
import { supabase } from "./supabase";
import { getActiveTenantId, setActiveTenantId } from "./tenant-context";

export type AppRole = "admin" | "approver" | "coordinator" | "payroll" | "employee";

export interface Membership {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  timezone: string;
  roles: AppRole[];
}

/** Rollen, die die Verwaltungsoberfläche (/app) öffnen dürfen. */
export const OFFICE_ROLES: AppRole[] = ["admin", "approver", "payroll"];

interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
  memberships: Membership[];
  membershipsLoading: boolean;
  membershipsError: Error | null;
  tenant: Membership | null;
  hasRole: (...roles: AppRole[]) => boolean;
  switchTenant: (tenantId: string) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(getActiveTenantId());

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => data.subscription.unsubscribe();
  }, []);

  const userId = session?.user.id ?? null;
  const membershipsQuery = useQuery({
    queryKey: qk.memberships(userId),
    enabled: userId !== null,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("my_memberships");
      if (error) throw error;
      return (data ?? []) as Membership[];
    },
  });
  const memberships = useMemo(() => membershipsQuery.data ?? [], [membershipsQuery.data]);

  // Gespeicherter Mandant nur, wenn der Benutzer dort (noch) Mitglied ist; bei genau einem automatisch.
  const tenant = useMemo(() => {
    const stored = memberships.find((m) => m.tenant_id === activeId);
    if (stored) return stored;
    return memberships.length === 1 ? memberships[0]! : null;
  }, [memberships, activeId]);

  useEffect(() => {
    if (tenant && tenant.tenant_id !== getActiveTenantId()) setActiveTenantId(tenant.tenant_id);
  }, [tenant]);

  const switchTenant = useCallback(
    (tenantId: string) => {
      setActiveTenantId(tenantId);
      setActiveId(tenantId);
      // Alles Mandantenbezogene neu laden; die Schlüssel tragen die Mandanten-ID ohnehin.
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "memberships" });
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setActiveTenantId(null);
    setActiveId(null);
    queryClient.clear();
  }, [queryClient]);

  const hasRole = useCallback((...roles: AppRole[]) => roles.some((r) => tenant?.roles.includes(r)), [tenant]);

  const value: AuthState = {
    session,
    user: session?.user ?? null,
    loading,
    memberships,
    membershipsLoading: membershipsQuery.isLoading,
    membershipsError: membershipsQuery.error,
    tenant,
    hasRole,
    switchTenant,
    signOut,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth außerhalb von AuthProvider");
  return ctx;
}

/** Für Seiten innerhalb der geschützten Bereiche: Mandant ist garantiert gesetzt. */
export function useTenant(): Membership {
  const { tenant } = useAuth();
  if (!tenant) throw new Error("useTenant ohne aktiven Mandanten");
  return tenant;
}
